import React, { ReactNode, useRef } from 'react'
import { Transition } from 'react-transition-group'

import { Headline, UiCloseIcon } from '@octane/spark'
import Portal from 'components/Portal'
import { useTheme } from 'styled-components'

import {
    GlobalStyle,
    PortalContent,
    Overlay,
    ContentWrapper,
    ModalWrapper,
    Header,
} from './Modal.styled'

export interface ModalProps {
    title?: string
    isOpen: boolean
    onClose: () => void
    showCloseButton?: boolean
    children: ReactNode
}

export default function Modal({
    title,
    isOpen,
    onClose,
    showCloseButton,
    children,
}: ModalProps) {
    const theme = useTheme()
    const portalContentRef = useRef<HTMLDivElement>(null)

    return (
        <>
            <GlobalStyle isOpen={isOpen} />
            <Transition
                nodeRef={portalContentRef}
                in={isOpen}
                timeout={200}
                unmountOnExit
                appear
            >
                {(state) => (
                    <Portal>
                        <PortalContent ref={portalContentRef}>
                            <ModalWrapper>
                                <Overlay
                                    onClick={onClose}
                                    state={state}
                                    data-testid="overlay"
                                />
                                <ContentWrapper state={state}>
                                    <Header>
                                        {title?.trim() && (
                                            <Headline variant="h36" as="h2">
                                                {title}
                                            </Headline>
                                        )}
                                        {showCloseButton && (
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                style={{ marginLeft: 'auto' }}
                                                data-testid="modal-close-btn"
                                            >
                                                <UiCloseIcon
                                                    width={20}
                                                    height={20}
                                                    color={
                                                        theme.colors.brand
                                                            .primary
                                                    }
                                                />
                                            </button>
                                        )}
                                    </Header>
                                    {children}
                                </ContentWrapper>
                            </ModalWrapper>
                        </PortalContent>
                    </Portal>
                )}
            </Transition>
        </>
    )
}

Modal.defaultProps = {
    showCloseButton: true,
}
